import { Box, Stack, Typography, useTheme } from "@mui/material";
import { Button, DataTable, SelectField } from "components";
import {
  useGetAllCustomers,
  useGetLevelCustomers,
  useGetLevelMappedCustomers,
  useGlobalStore,
  useMapLevelCustomers,
} from "controller";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import toast from "react-hot-toast";
import {
  LevelCustomer,
  LevelCustomerMappingRow,
  LevelMappedCustomer,
  MapLevelCustomersInput,
} from "shared/types/integrations";
import { useDebounce } from "utilz/helpers/hooks/useDebounceHook";
import { LEVEL_SKELETON_ROWS } from "./constants";
// import { useInView } from "react-intersection-observer";

const TableHeaders = [
  {
    name: "Level RMM Customer",
    sortable: false,
    key: "external_customer_name",
  },
  {
    name: "DeskDay Customer",
    sortable: false,
    key: "ddCustomer",
  },
];

export const LevelCustomerMapping = forwardRef(
  ({ onStepChange }: { onStepChange: (step: number) => void }, ref) => {
    const theme = useTheme();
    const mspId = useGlobalStore((state) => state.mspId);
    const { mutateAsync: mapCustomer } = useMapLevelCustomers();
    // Debounced server-side search — keeps customers beyond the first page
    // reachable instead of filtering only the loaded page.
    const [customerSearch, setCustomerSearch] = useDebounce("", 400);
    const { data: customers, isFetching: isCustomersFetching } =
      useGetAllCustomers({
        pageNumber: 1,
        perPageCount: 2000,
        sortColumn: "customer_name",
        sortDirection: "asc",
        filter: [{ column: "is_active", operator: "=", value: "true" }],
        searchQuery: customerSearch,
      });
    const {
      data: levelCustomersData,
      isLoading: isLevelCustomersLoading,
      fetchNextPage: fetchLevelCustomersNextPage,
      isFetchingNextPage: isFetchingLevelCustomersNextPage,
      hasNextPage,
    } = useGetLevelCustomers(100);

    // const { ref: loadMoreRef, inView } = useInView({
    //   threshold: 0.1,
    //   triggerOnce: false,
    //   onChange: (inView) => {
    //     console.log("In view:", inView);
    //     if (inView && !isFetchingLevelCustomersNextPage) {
    //       console.log("Fetching next page...");
    //       // if (levelCustomersData?.pages?.[-1]?.data?.length > 0) {
    //       fetchLevelCustomersNextPage();
    //       // }
    //     }
    //   },
    // });

    // useEffect(() => {
    //   if (inView && !isFetchingLevelCustomersNextPage) {
    //     fetchLevelCustomersNextPage();
    //   }
    // }, [inView, isFetchingLevelCustomersNextPage, fetchLevelCustomersNextPage]);

    const {
      data: levelMappedCustomers,
      isLoading: isLevelMappedCustomersLoading,
    } = useGetLevelMappedCustomers();
    const [transformedCustomerData, setTransformedCustomerData] = useState<
      LevelCustomerMappingRow[]
    >([]);

    // The rows are built in the effects below, a render after the queries
    // settle. Keep the skeleton up until they exist.
    const isBuildingRows =
      Boolean(levelCustomersData) &&
      Boolean(levelMappedCustomers) &&
      transformedCustomerData.length === 0;
    const [allLevelCustomers, setAllLevelCustomers] = useState<LevelCustomer[]>(
      [],
    );

    useEffect(() => {
      if (levelCustomersData?.pages) {
        const allCustomers = levelCustomersData.pages.flatMap(
          (page) => page?.data || [],
        );
        setAllLevelCustomers([
          ...allCustomers,
          {
            id: mspId,
            name: "Ungrouped Devices",
          },
        ]);
      }
    }, [levelCustomersData]);

    useEffect(() => {
      if (allLevelCustomers.length > 0 && levelMappedCustomers) {
        createMappingArray();
      }
    }, [allLevelCustomers, levelMappedCustomers]);

    const createMappingArray = () => {
      const customerArray = allLevelCustomers?.map(
        (customer: LevelCustomer) => {
          const customerIndex = levelMappedCustomers?.findIndex(
            (mappedCustomer: LevelMappedCustomer) =>
              mappedCustomer.external_customer_id === customer.id,
          );
          const customerObject: LevelCustomerMappingRow = {
            deskday_customer_id:
              customerIndex > -1
                ? levelMappedCustomers[customerIndex].deskday_customer_id
                : "",
            deskday_customer_name:
              customerIndex > -1
                ? levelMappedCustomers[customerIndex].deskday_customer_name
                : "",
            external_customer_id: customer.id,
            external_customer_name: customer.name,
          };
          return customerObject;
        },
      );
      setTransformedCustomerData(customerArray || []);
    };

    const addCustomerMapping = ({
      rowData,
      customerId,
      rowIndex,
    }: {
      rowData: LevelCustomerMappingRow;
      customerId: string | null;
      rowIndex: number;
    }) => {
      if (!customerId) {
        setTransformedCustomerData((prev) => {
          const newPrev = [...prev];
          newPrev[rowIndex].deskday_customer_id = customerId;
          newPrev[rowIndex].deskday_customer_name = null;
          return newPrev;
        });
        return;
      }
      const customerIndex = customers?.result.findIndex(
        (customer: { mapping_id: string }) =>
          customer.mapping_id === customerId,
      );
      if (customerIndex !== -1) {
        setTransformedCustomerData((prev) => {
          const newPrev = [...prev];
          newPrev[rowIndex].deskday_customer_id = customerId;
          newPrev[rowIndex].deskday_customer_name =
            customers?.result[customerIndex].customer_name;
          return newPrev;
        });
      }
    };

    const cellBuilders = {
      ddCustomer: (rowData: LevelCustomerMappingRow, index?: number) => {
        return (
          <SelectField
            sx={{ width: "180px" }}
            name="customer"
            hideHelperBox
            options={customers?.result || []}
            isSearchable
            isLoading={isCustomersFetching}
            onSearchValueChange={setCustomerSearch}
            // Search is served by the backend (`searchQuery`), so `onFilterValue`
            // returns true to stop the client from re-filtering the results.
            onFilterValue={() => true}
            value={rowData.deskday_customer_id}
            size="small"
            isResponsive={false}
            labelMappingKey="customer_name"
            valueMappingKey="mapping_id"
            // The options list shrinks while a search is active, so fall back to
            // the name held in row state to keep every row's label stable.
            renderValue={(_value, optionValue) =>
              optionValue?.customer_name || rowData.deskday_customer_name || ""
            }
            searchFieldProps={{
              placeholder: "Search customer",
            }}
            onValueChange={(value) =>
              addCustomerMapping({
                rowData,
                customerId: value,
                rowIndex: index || 0,
              })
            }
            isClearable
          />
        );
      },
    };

    const handleTransformForSubmit = (): MapLevelCustomersInput => {
      const submitArray = transformedCustomerData
        .filter((customer) => customer.deskday_customer_id)
        .map((customer) => ({
          external_customer_id: customer.external_customer_id,
          external_customer_name: customer.external_customer_name,
          deskday_customer_id: customer.deskday_customer_id ?? "",
          deskday_customer_name: customer.deskday_customer_name ?? "",
        }));
      return submitArray;
    };

    const onSubmit = async () => {
      try {
        const mappedCustomers = handleTransformForSubmit();
        await mapCustomer(mappedCustomers);
        toast.success("Customers mapped successfully");
        return true;
      } catch {
        toast.error("Failed to map customers");
        return false;
      }
    };

    useImperativeHandle(ref, () => ({
      onSubmit,
    }));

    return (
      <Stack
        sx={{ px: "32px", py: "24px", gap: "16px", height: "100%" }}
        position="relative"
      >
        <Typography variant="h6">Customer mapping</Typography>
        <Typography variant="body-medium" color={theme.palette.lightBg.low}>
          Please map your customers with corresponding customer names in Level
          RMM
        </Typography>
        <Box
          sx={{
            flex: "1",
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ flex: 1, overflow: "auto" }}>
            <DataTable
              isDataLoading={
                isLevelCustomersLoading ||
                isLevelMappedCustomersLoading ||
                isBuildingRows
              }
              headers={TableHeaders || []}
              data={transformedCustomerData || []}
              uniqueKey="external_customer_id"
              cellBuilders={cellBuilders}
              selectedItems={[]}
              currentPageNumber={0}
              itemsPerPage={LEVEL_SKELETON_ROWS}
              multiselect={false}
              // containerStyles={{
              //   height: "100%",
              //   minHeight: "200px",
              //   paddingBottom: "58px",
              // }}
              containerStyles={{
                height: {
                  xs: "calc(100vh - 310px)",
                  sm: "calc(100vh - 310px)",
                  md: "calc(100vh - 312px)",
                  lg: "calc(100vh - 310px)",
                },
                paddingBottom: "16px",
              }}
              headerCellStyles={{
                textTransform: "none",
              }}
            />
            {hasNextPage && (
              <Stack
                justifyContent={"center"}
                alignItems={"center"}
                position="absolute"
                bottom="8px"
                left={"50%"}
                sx={{
                  transform: "translateX(-50%)",
                  zIndex: 1000,
                }}
              >
                {/* <Button
                  variant="secondary"
                  onClick={() => fetchLevelCustomersNextPage()}
                  disabled={isFetchingLevelCustomersNextPage}
                  loading={isFetchingLevelCustomersNextPage}
                  sx={{
                    bgcolor: theme.palette.neutral.main,
                  }}
                >
                  {isFetchingLevelCustomersNextPage
                    ? "Loading More Customers"
                    : "Load More Customers"}
                </Button> */}
                <Button
                  variant="ghost"
                  onClick={() => fetchLevelCustomersNextPage()}
                  disabled={isFetchingLevelCustomersNextPage}
                  loading={isFetchingLevelCustomersNextPage}
                  // endIcon={<ExpandMoreIcon />}
                  sx={{
                    textTransform: "none",
                    color: theme.palette.primary.main,
                    "&:hover": {
                      backgroundColor: "transparent",
                      textDecoration: "underline",
                    },
                  }}
                >
                  {isFetchingLevelCustomersNextPage
                    ? "Loading..."
                    : "Load More Customers"}
                </Button>
              </Stack>
            )}
            {/* Move the loadMoreRef inside the scrollable area */}
            {/* <div
              ref={loadMoreRef}
              style={{
                height: "20px",
                marginTop: "10px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {isFetchingLevelCustomersNextPage && (
                <CircularProgress size={24} />
              )}
            </div> */}
          </Box>
        </Box>
      </Stack>
    );
  },
);
